<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content admin-panel">
        <pageaddheader title="Tenants" title1="Create and manage platform tenants" :items="[]" />

        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <div class="tenant-summary">
          <div v-for="card in tenantCards" :key="card.label" class="summary-card">
            <span class="summary-icon" :class="card.tone"><vue-feather :type="card.icon"></vue-feather></span>
            <div>
              <strong>{{ loading ? '-' : card.value }}</strong>
              <span>{{ card.label }}</span>
            </div>
          </div>
        </div>

        <div class="card table-card">
          <div class="card-body">
            <div class="table-top">
              <div class="search-input">
                <a class="btn btn-searchset">
                  <img src="../../../assets/img/icons/search-white.svg" alt="search" />
                </a>
                <input v-model="search" type="search" class="form-control form-control-sm" placeholder="Search tenants, plan, status" />
              </div>
              <div class="table-actions">
                <button class="btn btn-outline-secondary btn-sm" type="button" @click="fetchTenants" :disabled="loading">
                  {{ loading ? 'Refreshing...' : 'Refresh' }}
                </button>
                <button class="btn btn-added" type="button" @click="openCreate">
                  <img src="../../../assets/img/icons/plus.svg" class="me-2" alt="add" />
                  Create Tenant
                </button>
              </div>
            </div>

            <div v-if="loading" class="state-wrap">Loading tenants...</div>

            <div v-else class="table-responsive">
              <table class="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Users</th>
                    <th>Locations</th>
                    <th>Created</th>
                    <th class="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tenant in filteredTenants" :key="tenant.tenantid">
                    <td>
                      <div class="tenant-cell">
                        <span class="tenant-avatar">{{ initials(tenant.tenantname) }}</span>
                        <div>
                          <strong>{{ tenant.tenantname }}</strong>
                          <small>ID: {{ tenant.tenantid }}</small>
                        </div>
                      </div>
                    </td>
                    <td><span class="plan-pill">{{ tenant.plan }}</span></td>
                    <td>
                      <span class="status-pill" :class="statusClass(tenant.status)">{{ tenant.status }}</span>
                    </td>
                    <td>
                      <div class="usage-cell">
                        <strong>{{ tenant.users_count }} / {{ tenant.max_users }}</strong>
                        <span><i :style="{ width: usagePercent(tenant.users_count, tenant.max_users) }"></i></span>
                      </div>
                    </td>
                    <td>
                      <div class="usage-cell">
                        <strong>{{ tenant.locations_count }} / {{ tenant.max_locations }}</strong>
                        <span><i :style="{ width: usagePercent(tenant.locations_count, tenant.max_locations) }"></i></span>
                      </div>
                    </td>
                    <td>{{ formatDate(tenant.created_at) }}</td>
                    <td class="text-end">
                      <div class="action-row">
                        <router-link class="action-btn" :to="{ name: 'admin-tenant-details', params: { tenantId: tenant.tenantid } }" title="View">
                          <img src="../../../assets/img/icons/eye.svg" alt="view" />
                        </router-link>
                        <button class="action-btn" type="button" @click="openEdit(tenant)" title="Edit">
                          <img src="../../../assets/img/icons/edit.svg" alt="edit" />
                        </button>
                        <button v-if="tenant.status !== 'SUSPENDED'" class="action-btn warning" type="button" @click="suspend(tenant)" title="Suspend">
                          <vue-feather type="pause-circle"></vue-feather>
                        </button>
                        <button v-else class="action-btn success" type="button" @click="activate(tenant)" title="Activate">
                          <vue-feather type="play-circle"></vue-feather>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!filteredTenants.length">
                    <td colspan="7" class="text-center text-muted py-4">No tenants found.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-if="showModal" class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
          <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content">
              <div class="modal-header">
                <div>
                  <h5 class="modal-title">{{ editingTenant ? 'Edit Tenant' : 'Create Tenant' }}</h5>
                  <p class="modal-subtitle">{{ editingTenant ? 'Update plan and tenant limits' : 'Add company, first admin and default location' }}</p>
                </div>
                <button class="close" type="button" aria-label="Close" @click="closeModal"><span aria-hidden="true">x</span></button>
              </div>
              <form @submit.prevent="saveTenant">
                <div class="modal-body">
                  <div class="form-section">
                    <div class="section-title">
                      <span><vue-feather type="briefcase"></vue-feather></span>
                      <h6>Tenant information</h6>
                    </div>
                    <div class="row">
                      <div class="col-lg-6 col-sm-12">
                        <div class="form-group">
                          <label>Tenant Name</label>
                          <input v-model.trim="form.tenant_name" class="form-control" required />
                        </div>
                      </div>
                      <div class="col-lg-6 col-sm-12">
                        <div class="form-group">
                          <label>Plan</label>
                          <select v-model="form.plan" class="form-control">
                            <option>BASIC</option>
                            <option>PRO</option>
                            <option>ENTERPRISE</option>
                          </select>
                        </div>
                      </div>
                      <div class="col-lg-6 col-sm-12">
                        <div class="form-group">
                          <label>Max Users</label>
                          <input v-model.number="form.max_users" class="form-control" min="1" type="number" required />
                        </div>
                      </div>
                      <div class="col-lg-6 col-sm-12">
                        <div class="form-group">
                          <label>Max Locations</label>
                          <input v-model.number="form.max_locations" class="form-control" min="1" type="number" required />
                        </div>
                      </div>
                    </div>
                  </div>

                  <template v-if="!editingTenant">
                    <div class="form-section">
                      <div class="section-title">
                        <span><vue-feather type="user"></vue-feather></span>
                        <h6>First Admin User</h6>
                      </div>
                      <div class="row">
                        <div class="col-lg-4 col-sm-12">
                          <div class="form-group">
                            <label>Admin Name</label>
                            <input v-model.trim="form.admin_name" class="form-control" required />
                          </div>
                        </div>
                        <div class="col-lg-4 col-sm-12">
                          <div class="form-group">
                            <label>Email</label>
                            <input v-model.trim="form.admin_email" class="form-control" required type="email" />
                          </div>
                        </div>
                        <div class="col-lg-4 col-sm-12">
                          <div class="form-group">
                            <label>Password</label>
                            <input v-model="form.admin_password" class="form-control" minlength="6" required type="password" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="form-section">
                      <div class="section-title">
                        <span><vue-feather type="home"></vue-feather></span>
                        <h6>Company Information</h6>
                      </div>
                      <div class="row">
                        <div class="col-lg-6 col-sm-12">
                          <div class="form-group">
                            <label>Company Name</label>
                            <input v-model.trim="form.company_name" class="form-control" required />
                          </div>
                        </div>
                        <div class="col-lg-6 col-sm-12">
                          <div class="form-group">
                            <label>Phone</label>
                            <input v-model.trim="form.phone" class="form-control" required />
                          </div>
                        </div>
                        <div class="col-lg-8 col-sm-12">
                          <div class="form-group">
                            <label>Address</label>
                            <input v-model.trim="form.address" class="form-control" required />
                          </div>
                        </div>
                        <div class="col-lg-4 col-sm-12">
                          <div class="form-group">
                            <label>GSTIN</label>
                            <input v-model.trim="form.gstin" class="form-control" required />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="form-section">
                      <div class="section-title">
                        <span><vue-feather type="map-pin"></vue-feather></span>
                        <h6>Default Location</h6>
                      </div>
                      <div class="row">
                        <div class="col-lg-6 col-sm-12">
                          <div class="form-group">
                            <label>Location Name</label>
                            <input v-model.trim="form.location_name" class="form-control" required />
                          </div>
                        </div>
                        <div class="col-lg-6 col-sm-12">
                          <div class="form-group">
                            <label>Location Type</label>
                            <select v-model="form.location_type" class="form-control">
                              <option>STORE</option>
                              <option>WAREHOUSE</option>
                              <option>GODOWN</option>
                              <option>COUNTER</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
                <div class="modal-footer">
                  <button class="btn btn-secondary" type="button" @click="closeModal">Cancel</button>
                  <button class="btn btn-submit" type="submit" :disabled="saving">
                    {{ saving ? 'Saving...' : 'Save Tenant' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div v-if="showModal" class="modal-backdrop fade show"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { activateTenant, createTenant, listTenants, suspendTenant, updateTenant } from '@/services/superAdmin';
import Swal from 'sweetalert2';

const blankForm = () => ({
  tenant_name: '',
  plan: 'BASIC',
  max_users: 5,
  max_locations: 1,
  admin_name: '',
  admin_email: '',
  admin_password: '',
  company_name: '',
  phone: '',
  address: '',
  gstin: '',
  location_name: '',
  location_type: 'STORE',
});

export default {
  name: 'SuperAdminTenants',
  data() {
    return {
      tenants: [],
      loading: false,
      saving: false,
      error: '',
      search: '',
      showModal: false,
      editingTenant: null,
      form: blankForm(),
    };
  },
  computed: {
    filteredTenants() {
      const term = this.search.trim().toLowerCase();
      if (!term) {
        return this.tenants;
      }
      return this.tenants.filter((tenant) => {
        return [
          tenant.tenantname,
          tenant.plan,
          tenant.status,
          tenant.tenantid,
        ].join(' ').toLowerCase().includes(term);
      });
    },
    tenantCards() {
      return [
        { label: 'Total Tenants', value: this.tenants.length, icon: 'briefcase', tone: 'orange' },
        { label: 'Active', value: this.countByStatus('ACTIVE'), icon: 'check-circle', tone: 'green' },
        { label: 'Suspended', value: this.countByStatus('SUSPENDED'), icon: 'pause-circle', tone: 'red' },
        { label: 'Trial', value: this.countByStatus('TRIAL'), icon: 'clock', tone: 'blue' },
      ];
    },
  },
  mounted() {
    this.fetchTenants();
  },
  methods: {
    async fetchTenants() {
      this.loading = true;
      this.error = '';
      try {
        this.tenants = await listTenants();
      } catch (error) {
        this.error = error?.response?.data?.detail || 'Unable to load tenants';
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.editingTenant = null;
      this.form = blankForm();
      this.showModal = true;
    },
    openEdit(tenant) {
      this.editingTenant = tenant;
      this.form = {
        ...blankForm(),
        tenant_name: tenant.tenantname,
        plan: tenant.plan,
        max_users: tenant.max_users,
        max_locations: tenant.max_locations,
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.error = '';
    },
    async saveTenant() {
      this.saving = true;
      this.error = '';
      try {
        if (this.editingTenant) {
          await updateTenant(this.editingTenant.tenantid, {
            tenantname: this.form.tenant_name,
            plan: this.form.plan,
            max_users: this.form.max_users,
            max_locations: this.form.max_locations,
          });
        } else {
          await createTenant(this.form);
        }
        this.closeModal();
        await this.fetchTenants();
      } catch (error) {
        this.error = error?.response?.data?.detail || 'Unable to save tenant';
      } finally {
        this.saving = false;
      }
    },
    async suspend(tenant) {
      const result = await Swal.fire({
        title: 'Suspend tenant?',
        text: `${tenant.tenantname} users will not be able to use the system.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#FF9F43',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Suspend',
      });
      if (!result.isConfirmed) {
        return;
      }
      await suspendTenant(tenant.tenantid);
      await this.fetchTenants();
    },
    async activate(tenant) {
      const result = await Swal.fire({
        title: 'Activate tenant?',
        text: `${tenant.tenantname} will regain access.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#FF9F43',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Activate',
      });
      if (!result.isConfirmed) {
        return;
      }
      await activateTenant(tenant.tenantid);
      await this.fetchTenants();
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString() : '';
    },
    countByStatus(status) {
      return this.tenants.filter((tenant) => tenant.status === status).length;
    },
    initials(value) {
      return String(value || 'T').trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
    },
    statusClass(status) {
      return String(status || '').toLowerCase();
    },
    usagePercent(value, total) {
      if (!total) {
        return '0%';
      }
      return `${Math.min(100, Math.round((Number(value || 0) / Number(total)) * 100))}%`;
    },
  },
};
</script>

<style scoped>
.admin-panel {
  background: #f7f8fa;
  min-height: calc(100vh - 60px);
}

.tenant-summary {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  margin-bottom: 24px;
}

.summary-card {
  align-items: center;
  background: #fff;
  border: 1px solid #edf0f3;
  border-radius: 8px;
  box-shadow: 0 5px 18px rgba(15, 23, 42, 0.04);
  display: flex;
  gap: 14px;
  min-height: 96px;
  padding: 18px;
}

.summary-icon {
  align-items: center;
  border-radius: 8px;
  display: inline-flex;
  flex: 0 0 44px;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.summary-icon :deep(svg),
.section-title :deep(svg),
.action-btn :deep(svg) {
  height: 18px;
  stroke-width: 2.4;
  width: 18px;
}

.summary-card strong,
.summary-card span {
  display: block;
}

.summary-card strong {
  color: #111827;
  font-size: 24px;
  font-weight: 800;
  line-height: 1.1;
}

.summary-card div > span {
  color: #637381;
  font-size: 13px;
  font-weight: 700;
  margin-top: 5px;
}

.orange { background: #fff7ed; color: #c2410c; }
.green { background: #ecfdf5; color: #15803d; }
.red { background: #fef2f2; color: #b91c1c; }
.blue { background: #eff6ff; color: #2563eb; }

.table-top,
.table-actions,
.search-input,
.action-row,
.tenant-cell {
  align-items: center;
  display: flex;
}

.table-top {
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 16px;
}

.search-input {
  gap: 6px;
  min-width: 280px;
}

.search-input input {
  max-width: 360px;
}

.table-actions,
.action-row {
  gap: 8px;
}

.table-card {
  border: 1px solid #edf0f3;
  border-radius: 8px;
}

.state-wrap {
  color: #6b7280;
  padding: 24px;
}

.tenant-cell {
  gap: 12px;
}

.tenant-avatar {
  align-items: center;
  background: #e9edf3;
  border-radius: 8px;
  color: #111827;
  display: inline-flex;
  flex: 0 0 40px;
  font-size: 12px;
  font-weight: 800;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.tenant-cell strong,
.tenant-cell small {
  display: block;
}

.tenant-cell small {
  color: #637381;
  margin-top: 4px;
}

.plan-pill {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #475569;
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  padding: 5px 9px;
}

.status-pill {
  border-radius: 6px;
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  padding: 5px 9px;
}

.status-pill.active {
  background: #e9f7f1;
  color: #12805c;
}

.status-pill.suspended {
  background: #fff1e6;
  color: #b54708;
}

.status-pill.trial {
  background: #eaf2ff;
  color: #175cd3;
}

.usage-cell strong {
  color: #111827;
  display: block;
  font-size: 13px;
  margin-bottom: 7px;
}

.usage-cell span {
  background: #eef0f4;
  border-radius: 999px;
  display: block;
  height: 6px;
  overflow: hidden;
  width: 96px;
}

.usage-cell i {
  background: #ff9f43;
  display: block;
  height: 100%;
}

.action-row {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.action-btn {
  align-items: center;
  background: #fff;
  border: 1px solid #e8ebed;
  border-radius: 6px;
  color: #637381;
  display: flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.action-btn.warning {
  color: #b54708;
}

.action-btn.success {
  color: #12805c;
}

.modal {
  background: rgba(15, 23, 42, 0.45);
  z-index: 2000;
}

.modal-backdrop {
  z-index: 1990;
}

.modal-content {
  border: 0;
  border-radius: 8px;
}

.modal-header {
  align-items: flex-start;
  border-bottom: 1px solid #eef0f4;
}

.modal-title {
  color: #111827;
  font-weight: 800;
}

.modal-subtitle {
  color: #637381;
  font-size: 12px;
  margin: 4px 0 0;
}

.close {
  background: transparent;
  border: 0;
  color: #637381;
  font-size: 22px;
  line-height: 1;
}

.form-section {
  border: 1px solid #eef0f4;
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 16px 16px 0;
}

.section-title {
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}

.section-title span {
  align-items: center;
  background: #fff6ee;
  border-radius: 8px;
  color: #f36f21;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.section-title h6 {
  color: #111827;
  font-weight: 800;
  margin: 0;
}

@media (max-width: 767px) {
  .search-input,
  .table-actions,
  .table-actions .btn {
    width: 100%;
  }
}
</style>
